import React, { useState } from 'react';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Box, Button, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router';

import { useBooleanStateWithToggle, useIsMounted } from '@paragon/client-tools';
import { hasMaterialsEditPermission } from '@paragon/login-tools';
import { TypographyWithActionButtons } from '@paragon/material';
import { enqueueError } from '@paragon/notification-tools';
import { runDelayedSpinner } from '@paragon/task-spinner';

import { AsyncThunkActionVoid } from '../../../actions/thunkActionTypes';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { DefaultListIcon } from '../cards/common/DefaultListIcon';
import { NameDialog } from '../NameDialog';
import { PossiblyArchivedListName } from '../PossiblyArchivedListName';

interface ListActionsProps {
  readonly type: 'lumber' | 'plate' | 'hanger';
  readonly list: {
    readonly guid: string;
    readonly name: string;
    readonly isArchived: boolean;
  };
  readonly defaultListGuid: string | null;

  setDefaultListGuid: (guid: string) => AsyncThunkActionVoid;
  archiveList: (guid: string) => AsyncThunkActionVoid;
  restoreList: (guid: string) => AsyncThunkActionVoid;
  duplicateList: (guid: string) => AsyncThunkActionVoid;
  renameList: (guid: string, name: string) => AsyncThunkActionVoid;
}

export function ListActions(props: ListActionsProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const hasEditPermission = useAppSelector(hasMaterialsEditPermission);

  const isMounted = useIsMounted();

  const [buttonBarActionOngoing, setButtonBarActionOngoing] = useState(false);
  const [renameDialogOpen, toggleRenameDialogOpen] = useBooleanStateWithToggle();

  const createButtonBarAction = (action: string, callback: () => Promise<void>) => () => {
    const spinnerMessage = `${action.charAt(0).toUpperCase() + action.slice(1)}...`;

    const cleanUpSpinner = dispatch(runDelayedSpinner(spinnerMessage));
    setButtonBarActionOngoing(true);

    callback()
      .catch((error) => {
        console.error(error);
        dispatch(enqueueError(`Error while ${action}. Please contact Paragon Support.`));
      })
      .finally(() => {
        cleanUpSpinner();
        if (isMounted()) setButtonBarActionOngoing(false);
      });
  };

  return (
    <>
      <TypographyWithActionButtons
        text={
          <>
            <PossiblyArchivedListName name={props.list.name} isArchived={props.list.isArchived} />
            <DefaultListIcon type={props.type} sx={{ ml: 1 }} visible={props.defaultListGuid === props.list.guid} />
          </>
        }
        variant="h3"
        pushButtonsRight
        gutterBottom
      >
        {hasEditPermission && (
          <Box
            sx={{
              '& > :not(:last-child)': {
                mr: 1,
              },
            }}
          >
            {props.defaultListGuid == null ? null : props.defaultListGuid === props.list.guid ? (
              <Tooltip
                disableInteractive
                title={`This list is already your organization's default ${props.type} list.`}
              >
                <span>
                  <Button disabled variant="outlined">
                    <StarIcon />
                    Default List
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                disabled={buttonBarActionOngoing}
                onClick={createButtonBarAction(`setting default ${props.type} list`, async () => {
                  await dispatch(props.setDefaultListGuid(props.list.guid));
                })}
              >
                <StarBorderIcon />
                Make Default
              </Button>
            )}

            <Button variant="outlined" disabled={buttonBarActionOngoing} onClick={toggleRenameDialogOpen}>
              Rename
            </Button>
            <Button
              variant="outlined"
              disabled={buttonBarActionOngoing}
              onClick={createButtonBarAction(`duplicating ${props.type} list`, async () => {
                await dispatch(props.duplicateList(props.list.guid));
                navigate('/');
              })}
            >
              Duplicate
            </Button>
            <Button
              variant="outlined"
              disabled={buttonBarActionOngoing}
              onClick={createButtonBarAction(`updating ${props.type} list`, async () => {
                if (props.list.isArchived) {
                  await dispatch(props.restoreList(props.list.guid));
                } else {
                  await dispatch(props.archiveList(props.list.guid));
                }
              })}
            >
              {props.list.isArchived ? 'Restore' : 'Archive'}
            </Button>
          </Box>
        )}
      </TypographyWithActionButtons>
      <NameDialog
        verb="rename"
        noun={`${props.type} list`}
        open={renameDialogOpen}
        defaultValue={props.list.name}
        onClose={toggleRenameDialogOpen}
        onSubmit={async (name) => {
          await dispatch(props.renameList(props.list.guid, name));
        }}
      />
    </>
  );
}
